import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UseGameRealTimeUpdatesProps {
  gameId?: string;
  onGameUpdate?: () => void;
  onRosterUpdate?: () => void;
  onPlayerStatusUpdate?: () => void;
  onConfirmationUpdate?: () => void;
  userId?: string;
}

export const useGameRealTimeUpdates = ({
  gameId,
  onGameUpdate,
  onRosterUpdate,
  onPlayerStatusUpdate,
  onConfirmationUpdate,
  userId
}: UseGameRealTimeUpdatesProps) => {
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('game-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'venue_games',
          filter: gameId ? `game_id=eq.${gameId}` : undefined
        },
        (payload) => {
          console.log('Game updated:', payload);
          const gameData = payload.new;
          
          if (gameData.game_status === 'confirmed') {
            toast.success('Game confirmed! Countdown timer started.');
          } else if (gameData.game_status === 'forfeited') {
            toast.error('Game forfeited due to insufficient players.');
          } else if (gameData.game_status === 'completed') {
            toast.info('Game completed! Check results in leaderboard.');
          }
          
          onGameUpdate?.();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_participants',
          filter: gameId ? `game_id=eq.${gameId}` : undefined
        },
        (payload) => {
          console.log('Game participants updated:', payload);
          
          if (payload.eventType === 'INSERT') {
            toast.info('New player added to game roster');
          } else if (payload.eventType === 'UPDATE') {
            const participant = payload.new;
            if (participant.participation_status === 'ready_to_play') {
              toast.success('Player confirmed as ready to play');
            } else if (participant.participation_status === 'no_show') {
              toast.warning('Player marked as no-show');
            }
            onPlayerStatusUpdate?.();
          } else if (payload.eventType === 'DELETE') {
            toast.info('Player removed from game roster');
          }
          
          onRosterUpdate?.();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_rosters',
          filter: gameId ? `game_id=eq.${gameId}` : undefined
        },
        (payload) => {
          console.log('Game rosters updated:', payload);
          
          if (payload.eventType === 'INSERT') {
            toast.success('Player added to roster');
          } else if (payload.eventType === 'DELETE') {
            toast.info('Player removed from roster');
          } else if (payload.eventType === 'UPDATE') {
            toast.info('Roster updated');
          }
          
          onRosterUpdate?.();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_confirmations',
          filter: gameId ? `game_id=eq.${gameId}` : undefined
        },
        (payload) => {
          console.log('Game confirmations updated:', payload);
          
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const confirmation = payload.new;
            if (confirmation.confirmation_status) {
              toast.success(`${confirmation.team_side} team confirmed participation`);
            }
          }
          
          onConfirmationUpdate?.();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'venue_game_confirmations',
          filter: gameId ? `game_id=eq.${gameId}` : undefined
        },
        (payload) => {
          console.log('Venue game confirmations updated:', payload);
          onConfirmationUpdate?.();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_notifications'
        },
        (payload) => {
          console.log('Game notification:', payload);
          
          const notification = payload.new as any;
          if (notification?.recipient_id === userId) {
            if (notification?.notification_type === 'game_reminder') {
              toast.info('Game reminder: Match starting soon!');
            } else if (notification?.notification_type === 'roster_deadline') {
              toast.warning('Roster deadline approaching!');
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [gameId, userId, onGameUpdate, onRosterUpdate, onPlayerStatusUpdate, onConfirmationUpdate]);
};